import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";

// Southeast Asian languages with their Google Translate codes
const languages = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "id", name: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "th", name: "ไทย", flag: "🇹🇭" },
  { code: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  { code: "tl", name: "Filipino", flag: "🇵🇭" },
  { code: "ms", name: "Bahasa Melayu", flag: "🇲🇾" },
  { code: "my", name: "မြန်မာ", flag: "🇲🇲" },
  { code: "km", name: "ភាសាខ្មែរ", flag: "🇰🇭" },
  { code: "lo", name: "ລາວ", flag: "🇱🇦" },
  { code: "zh-CN", name: "中文", flag: "🇨🇳" },
];

declare global {
  interface Window {
    googleTranslateElementInit: () => void;
    google: {
      translate: {
        TranslateElement: new (
          config: {
            pageLanguage: string;
            includedLanguages: string;
            autoDisplay: boolean;
          },
          elementId: string
        ) => void;
      };
    };
  }
}

export const LanguageSelector = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentLang, setCurrentLang] = useState("en");

  // Lazy-load Google Translate only when the user first interacts with the
  // language menu. This avoids ~360KB of unused JS on initial page load.
  const ensureLoaded = () => {
    if (document.getElementById("google-translate-script")) {
      return;
    }

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: languages.map((l) => l.code).join(","),
          autoDisplay: false,
        },
        "google_translate_element"
      );
      setIsLoaded(true);
    };

    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  };

  const translatePage = (langCode: string) => {
    setCurrentLang(langCode);
    ensureLoaded();

    const applyLang = (attempts = 0) => {
      const selectElement = document.querySelector(
        ".goog-te-combo"
      ) as HTMLSelectElement | null;

      if (selectElement) {
        selectElement.value = langCode;
        selectElement.dispatchEvent(new Event("change"));
      } else if (attempts < 40) {
        setTimeout(() => applyLang(attempts + 1), 150);
      }
    };
    applyLang();
  };

  const currentLanguage = languages.find((l) => l.code === currentLang);

  return (
    <>
      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" className="hidden" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-sm font-medium"
          >
            <span className="text-lg">{currentLanguage?.flag || "🌐"}</span>
            <Globe className="w-4 h-4 hidden sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => translatePage(lang.code)}
              className={`gap-3 cursor-pointer ${
                currentLang === lang.code ? "bg-muted" : ""
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Hide Google Translate bar and attribution */}
      <style>{`
        .goog-te-banner-frame,
        .goog-te-gadget-icon,
        #goog-gt-tt,
        .goog-te-balloon-frame,
        .goog-tooltip,
        .goog-tooltip:hover,
        .goog-text-highlight,
        .VIpgJd-ZVi9od-l4eHX-hSRGPd,
        .skiptranslate {
          display: none !important;
        }
        body {
          top: 0 !important;
        }
        .goog-te-gadget {
          font-size: 0 !important;
        }
      `}</style>
    </>
  );
};
